
import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Language } from '@/types';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import { Sparkles, GraduationCap, Map, Cpu, Zap, ArrowRight, User, Trophy, ShieldCheck } from 'lucide-react';

interface NovaAboutPageProps {
  lang: Language;
}

const NovaAboutPage: React.FC<NovaAboutPageProps> = ({ lang }) => {
  const navigate = useNavigate();
  const isRtl = lang === 'ar';

  const content = {
    en: {
      hero: {
        badge: "Our Story",
        title: "Built by a GIS student, for GIS students.",
      },
      story: {
        title: "The Problem",
        body: `I was in my third year at the GIS department, Cairo University.\n\nI used to spend hours searching through complex English documentation—and all the examples were from America or Europe. Nothing understood that I was working on Nile Delta data, that the projection used in Egypt is different, or that the crops I was studying were wheat and rice, not corn and soybeans.\n\nGeneral AI models answered—but they didn't understand. They gave me general answers without knowing that I was working in an ArcGIS Pro environment, or that my dataset was Sentinel-2, or that my project was about urban sprawl in Cairo.\n\nI decided to build the tool I needed.`
      },
      features: [
        {
          title: "Nova understands the Egyptian context",
          desc: "NDVI values in the Nile Delta, geographic projections used in Egypt, and local seasonal crops.",
          icon: <Map className="text-[#7F77DD]" />
        },
        {
          title: "Nova remembers you",
          desc: "It doesn't start from scratch every time—it knows your project, your level, and where you reached.",
          icon: <Cpu className="text-[#7F77DD]" />
        },
        {
          title: "Nova works with you",
          desc: "It doesn't just explain—it writes the code, plans the project, and generates the final report.",
          icon: <Zap className="text-[#7F77DD]" />
        }
      ],
      stats: [
        { label: "Supported Tools", value: "3", sub: "ArcGIS + QGIS + GEE" },
        { label: "Specialized Modes", value: "5", sub: "Study / Code / Project / Exam / Report" },
        { label: "Egypt Context Layer", value: "15+", sub: "Local Egyptian Crops" },
        { label: "Founded", value: "2026", sub: "Cairo, Egypt" }
      ],
      vexl: {
        title: "Nova and VEXL",
        body: "Nova is the first product of VEXL—the GeoAI platform.\n\nWe are building VEXL (Vector × Pixel) to transform geographic data into smart decisions—for students, professionals, and companies.\n\nNova is the beginning: The student learning with Nova today is the engineer who will use Atlas and Lens tomorrow."
      },
      founder: {
        name: "Abdurahman Diaa",
        title: "Founder, VEXL — GIS Student, Cairo University",
        achievement: "3rd Place Globally — Drought Watch Satellite ML Competition",
        quote: "I built Nova so none of my colleagues would waste their time searching like I did."
      },
      cta: {
        title: "Ready to try Nova?",
        subtitle: "Start for free — no credit card required.",
        button: "Start for Free →"
      }
    },
    ar: {
      hero: {
        badge: "قصتنا",
        title: "بناه طالب GIS — لطلاب GIS.",
      },
      story: {
        title: "المشكلة",
        body: `كنت في السنة الثالثة بقسم GIS في جامعة القاهرة.\n\nكنت بضيع ساعات أبحث في documentation إنجليزي معقد — وكل الأمثلة من أمريكا أو أوروبا. مفيش حاجة بتفهم إن أنا شغال على بيانات دلتا النيل، وإن الـ projection اللي بيستخدموه في مصر مختلف، وإن المحاصيل اللي بدرسها قمح وأرز مش corn وsoybeans.\n\nنماذج الذكاء الاصطناعي العامة كانت بتجاوب — بس مش بتفهم. كانت بتديني إجابات عامة من غير ما يعرف إني شغال في بيئة ArcGIS Pro، أو إن الـ dataset بتاعي Sentinel-2، أو إن مشروعي عن الزحف العمراني في القاهرة.\n\nقررت أبني الـ tool اللي كنت محتاجه.`
      },
      features: [
        {
          title: "نوفا يفهم السياق المصري",
          desc: "قيم NDVI في دلتا النيل، الإسقاطات الجغرافية المستخدمة في مصر، والمحاصيل الموسمية.",
          icon: <Map className="text-[#7F77DD]" />
        },
        {
          title: "نوفا يتذكرك",
          desc: "مش بيبدأ من الصفر كل مرة — بيعرف مشروعك ومستواك وفين وصلت.",
          icon: <Cpu className="text-[#7F77DD]" />
        },
        {
          title: "نوفا بيشتغل معاك",
          desc: "مش بس بيشرح — بيكتب الكود، بيخطط المشروع، وبيولد التقرير النهائي.",
          icon: <Zap className="text-[#7F77DD]" />
        }
      ],
      stats: [
        { label: "أدوات جغرافية مدعومة", value: "3", sub: "ArcGIS + QGIS + GEE" },
        { label: "أوضاع متخصصة", value: "5", sub: "Study / Code / Project / Exam / Report" },
        { label: "Egypt Context Layer", value: "15+", sub: "محصول مصري" },
        { label: "سنة التأسيس", value: "2026", sub: "القاهرة، مصر" }
      ],
      vexl: {
        title: "Nova وVEXL",
        body: "Nova هو أول منتج من VEXL — منصة الذكاء الاصطناعي الجغرافي.\n\nVEXL (Vector × Pixel) بنبنيها عشان نحول البيانات الجغرافية لقرارات ذكية — للطلاب، للمحترفين، وللشركات.\n\nNova هو البداية: الطالب اللي بيتعلم مع Nova النهارده هو المهندس اللي هيستخدم Atlas وLens بكره."
      },
      founder: {
        name: "عبدالرحمن ضياء",
        title: "Founder, VEXL — طالب GIS، جامعة القاهرة",
        achievement: "المركز الثالث عالمياً — مسابقة Drought Watch لتعلم الآلة للأقمار الصناعية",
        quote: "بنيت Nova عشان ما يضيعش حد من زمايلي وقته في البحث زي ما ضيّعت أنا."
      },
      cta: {
        title: "جاهز تجرب Nova؟",
        subtitle: "ابدأ مجاناً — مش محتاج كارت بنكي.",
        button: "ابدأ مجاناً ←"
      }
    }
  }[lang];

  return (
    <PageTransition>
      <SEO 
        title={lang === 'ar' ? 'عن نوفا | قصتنا' : 'About Nova | Our Story'}
        description="The story behind VEXL Nova - built by a GIS student for GIS students in Egypt."
        lang={lang}
      />
      
      <div className={`bg-[#02040a] min-h-screen text-white selection:bg-[#7F77DD]/30 ${isRtl ? 'font-arabic' : 'font-sans'}`} dir={isRtl ? 'rtl' : 'ltr'}>
        {/* Background Effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#7F77DD]/5 blur-[120px] rounded-full" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/5 blur-[100px] rounded-full" />
        </div>

        {/* Section 1: Hero */}
        <section className="relative pt-20 md:pt-24 pb-20 px-6 text-center">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7F77DD]/10 border border-[#7F77DD]/20 text-[#a79ef8] text-[10px] font-black uppercase tracking-[0.3em] mb-8"
            >
              <Sparkles size={10} /> {content.hero.badge}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[1.1]"
            >
              {isRtl ? (
                <>
                  بناه <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7F77DD] via-purple-400 to-cyan-400">طالب GIS</span> —
                  <br />
                  <span className="text-white/40">لطلاب GIS.</span>
                </>
              ) : (
                <>
                  Built by a <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7F77DD] via-purple-400 to-cyan-400">GIS student</span>,
                  <br />
                  <span className="text-white/40">for GIS students.</span>
                </>
              )}
            </motion.h1>
          </div>
        </section>

        {/* Section 2: The Problem Story */}
        <section className="relative py-24 px-6 bg-white/[0.01] border-y border-white/5">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: isRtl ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-10"
            >
              <div className="w-12 h-px bg-[#7F77DD]/40" />
              <h2 className="text-xl font-black uppercase tracking-[0.2em] text-[#7F77DD]">{content.story.title}</h2>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-6 text-lg md:text-xl text-slate-300 leading-relaxed font-medium"
            >
              {content.story.body.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Section 3: Features */}
        <section className="relative py-32 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-10 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-all group"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#7F77DD]/10 flex items-center justify-center mb-8 border border-[#7F77DD]/20 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed font-medium">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Section 4: Stats */}
        <section className="relative py-24 px-6 border-y border-white/5 bg-[#050608]">
          <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-12">
            {content.stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-black text-white mb-2 tracking-tighter">{stat.value}</div>
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7F77DD] mb-3">{stat.label}</div>
                <div className="text-xs text-slate-500 font-medium">{stat.sub}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Section 5: Nova and VEXL */}
        <section className="relative py-32 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-5xl font-black mb-12 tracking-tight"
            >
              {content.vexl.title}
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-8 text-lg md:text-xl text-slate-400 leading-relaxed font-medium"
            >
              {content.vexl.body.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Section: Proprietary Architecture & Sovereign IP */}
        <section className="relative py-24 px-6 bg-white/[0.01] border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-[#070912] to-[#04060a] border border-[#7F77DD]/20 shadow-2xl relative overflow-hidden space-y-6">
              
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono font-bold">
                  <ShieldCheck size={14} />
                  <span>{isRtl ? 'ملكية فكرية مسجلة ومحمية // إيداع رقم 5007' : 'REGISTERED SOVEREIGN IP // EGIPA #5007'}</span>
                </div>
                <span className="text-xs font-mono text-slate-500">Law No. 82/2002 & Law No. 163/2023</span>
              </div>

              <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-white">
                {isRtl ? 'بنية برمجية سيادية ومحمية قانونياً' : 'Proprietary Architecture & Sovereign IP'}
              </h2>

              <p className="text-base text-slate-300 leading-relaxed">
                {isRtl
                  ? 'منظومة VEXL هي أصل برمجي جيومكاني مسجل رسمياً لدى الجهاز المصري للملكية الفكرية (EGIPA برقم 5007). لا تعتمد المنظومة على مجرد تجميعة لمكتبات مفتوحة المصدر، بل تمتلك كوداً برمجياً أصيلاً ونماذج ذكاء اصطناعي مكانية سيادية محمية بموجب القانون.'
                  : 'VEXL Ecosystem is an officially registered software asset under the Egyptian Intellectual Property Authority (EGIPA Reg. #5007). VEXL is built from the ground up on proprietary mathematical foundations, time-series neural architectures, and sovereign data pipelines.'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-1.5">
                  <div className="text-xs font-mono text-[#7F77DD] font-bold">{isRtl ? 'حزمة VEXL NOVA' : 'VEXL NOVA Engine'}</div>
                  <p className="text-xs text-slate-400">
                    {isRtl ? 'أتمتة الأكواد المكانية ودمج المتجهات والخلايا الرقمية والتحقق الطوبولوجي.' : 'Automated spatial code generation & vector-raster geometry fusion.'}
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-1.5">
                  <div className="text-xs font-mono text-amber-400 font-bold">{isRtl ? 'حزمة Drought Watch Pro' : 'Drought Watch Pro'}</div>
                  <p className="text-xs text-slate-400">
                    {isRtl ? 'شبكات Bi-LSTM للتنبؤ البيئي والجفاف قبل 6 أشهر بدقة 91.3%.' : 'Deep Bi-LSTM time-series forecasting with 91.3% verified accuracy.'}
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/intellectual-property"
                  className="inline-flex items-center gap-2 text-xs font-bold text-[#7F77DD] hover:underline"
                >
                  <span>{isRtl ? 'عرض ملف الملكية الفكرية والشهادة الرسمية' : 'View Full Intellectual Property Dossier & Certificate'}</span>
                  <ArrowRight size={13} className={isRtl ? 'rotate-180' : ''} />
                </Link>
              </div>

            </div>
          </div>
        </section>

        {/* Section 6: Founder */}
        <section className="relative py-32 px-6 bg-white/[0.01] border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-10 md:p-16 rounded-[3rem] bg-gradient-to-br from-[#0a0c14] to-[#050608] border border-white/10 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-8 opacity-5">
                <GraduationCap size={200} />
              </div>
              
              <div className="flex flex-col md:flex-row items-center gap-12 relative z-10">
                <div className="w-48 h-48 rounded-full border-4 border-[#7F77DD]/20 overflow-hidden shrink-0 shadow-2xl">
                   <div className="w-full h-full bg-[#0d0f1a] flex items-center justify-center text-5xl font-black text-[#7F77DD]/30">
                      AD
                   </div>
                </div>
                
                <div className={`text-center ${isRtl ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="text-2xl font-black text-white mb-2">{content.founder.name}</div>
                  <div className="text-sm font-bold text-[#7F77DD] uppercase tracking-widest mb-4">{content.founder.title}</div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-black uppercase tracking-wider mb-8">
                    <Trophy size={10} />
                    <span>{content.founder.achievement}</span>
                  </div>
                  <p className="text-xl md:text-2xl italic text-slate-300 font-medium leading-relaxed">
                    "{content.founder.quote}"
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section 7: Final CTA */}
        <section className="relative py-40 px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter">{content.cta.title}</h2>
            <p className="text-slate-400 mb-12 font-medium">{content.cta.subtitle}</p>
            
            <button
              onClick={() => navigate('/nova-workspace')}
              className="px-12 py-5 rounded-2xl bg-[#7F77DD] text-white font-black uppercase tracking-widest text-[12px] shadow-[0_0_40px_rgba(127,119,221,0.3)] hover:scale-[1.05] transition-all"
            >
              {content.cta.button}
            </button>
          </motion.div>
        </section>

      </div>
    </PageTransition>
  );
};

export default NovaAboutPage;
